/**
 * FelixDog — Animated Bernedoodle thinking indicator for Felix chat.
 *
 * States:
 * - "walking" — Dog trots back and forth while Felix is thinking
 * - "dead" — Dog flips on its back with X eyes and legs up (error state)
 * - "idle" — Dog sits still (not shown normally, but available)
 */

import { memo } from "react";

type DogState = "walking" | "dead" | "idle";

interface FelixDogProps {
  state: DogState;
  /** Optional label displayed next to the dog */
  label?: string;
}

/**
 * Tiny inline SVG Bernedoodle (black, white, tan).
 * 80×40 viewbox keeps it small beneath the chat messages.
 */
function WalkingDog() {
  return (
    <svg
      viewBox="0 0 80 44"
      width="80"
      height="44"
      className="animate-felix-walk"
      aria-hidden="true"
    >
      {/* Body — black oval */}
      <ellipse cx="40" cy="22" rx="18" ry="10" fill="#2d2926" />
      {/* Belly patch — white */}
      <ellipse cx="40" cy="26" rx="10" ry="5" fill="#f5f0e8" />
      {/* Tan saddle */}
      <ellipse cx="36" cy="19" rx="8" ry="5" fill="#c4854c" opacity="0.8" />

      {/* Head — black circle */}
      <circle cx="58" cy="16" r="8" fill="#2d2926" />
      {/* Snout — tan */}
      <ellipse cx="63" cy="18" rx="4.5" ry="3" fill="#c4854c" />
      {/* Nose */}
      <ellipse cx="65.5" cy="17" rx="1.5" ry="1" fill="#1a1a1a" />
      {/* Eye — shiny */}
      <circle cx="59" cy="14" r="1.5" fill="#f5f0e8" />
      <circle cx="59" cy="14" r="0.8" fill="#1a1a1a" />
      {/* Ear — floppy */}
      <ellipse cx="53" cy="11" rx="3.5" ry="5" fill="#2d2926" transform="rotate(-15 53 11)" />

      {/* Tail — wagging */}
      <path
        d="M22 18 Q16 8 12 12"
        stroke="#2d2926"
        strokeWidth="3"
        strokeLinecap="round"
        fill="none"
        className="animate-felix-tail"
      />

      {/* Front legs — walking animation */}
      <g className="animate-felix-front-legs">
        <line x1="50" y1="30" x2="52" y2="42" stroke="#2d2926" strokeWidth="3" strokeLinecap="round" />
        <line x1="44" y1="30" x2="42" y2="42" stroke="#2d2926" strokeWidth="3" strokeLinecap="round" />
      </g>
      {/* Back legs — walking animation (opposite phase) */}
      <g className="animate-felix-back-legs">
        <line x1="36" y1="30" x2="38" y2="42" stroke="#2d2926" strokeWidth="3" strokeLinecap="round" />
        <line x1="30" y1="30" x2="28" y2="42" stroke="#2d2926" strokeWidth="3" strokeLinecap="round" />
      </g>

      {/* Tongue — happy boy */}
      <ellipse cx="65" cy="20.5" rx="1.2" ry="2" fill="#e8636f" className="animate-felix-tongue" />
    </svg>
  );
}

function DeadDog() {
  return (
    <svg
      viewBox="0 0 80 44"
      width="80"
      height="44"
      aria-hidden="true"
      className="animate-felix-fall"
    >
      {/* Flipped upside down — rotated 180 on Y so belly faces up */}
      <g transform="translate(0, 44) scale(1, -1)">
        {/* Body — black oval */}
        <ellipse cx="40" cy="24" rx="18" ry="10" fill="#2d2926" />
        {/* Belly — white (now facing up) */}
        <ellipse cx="40" cy="20" rx="12" ry="6" fill="#f5f0e8" />
        {/* Tan patch */}
        <ellipse cx="36" cy="27" rx="8" ry="5" fill="#c4854c" opacity="0.6" />

        {/* Head */}
        <circle cx="58" cy="28" r="8" fill="#2d2926" />
        {/* Snout */}
        <ellipse cx="63" cy="26" rx="4.5" ry="3" fill="#c4854c" />
        {/* Nose */}
        <ellipse cx="65.5" cy="27" rx="1.5" ry="1" fill="#1a1a1a" />

        {/* X eyes — dead */}
        <g stroke="#f5f0e8" strokeWidth="1.2" strokeLinecap="round">
          <line x1="57" y1="30" x2="59.5" y2="32.5" />
          <line x1="59.5" y1="30" x2="57" y2="32.5" />
        </g>

        {/* Tongue hanging out */}
        <ellipse cx="64.5" cy="23" rx="1.5" ry="2.5" fill="#e8636f" />

        {/* Legs sticking straight up — stiff */}
        <line x1="50" y1="14" x2="50" y2="2" stroke="#2d2926" strokeWidth="3" strokeLinecap="round" />
        <line x1="44" y1="14" x2="44" y2="2" stroke="#2d2926" strokeWidth="3" strokeLinecap="round" />
        <line x1="36" y1="14" x2="36" y2="2" stroke="#2d2926" strokeWidth="3" strokeLinecap="round" />
        <line x1="30" y1="14" x2="30" y2="2" stroke="#2d2926" strokeWidth="3" strokeLinecap="round" />

        {/* Ear flopped */}
        <ellipse cx="53" cy="33" rx="3.5" ry="5" fill="#2d2926" transform="rotate(15 53 33)" />
      </g>
    </svg>
  );
}

function IdleDog() {
  return (
    <svg viewBox="0 0 80 44" width="80" height="44" aria-hidden="true">
      {/* Body */}
      <ellipse cx="40" cy="22" rx="18" ry="10" fill="#2d2926" />
      <ellipse cx="40" cy="26" rx="10" ry="5" fill="#f5f0e8" />
      <ellipse cx="36" cy="19" rx="8" ry="5" fill="#c4854c" opacity="0.8" />

      {/* Head */}
      <circle cx="58" cy="16" r="8" fill="#2d2926" />
      <ellipse cx="63" cy="18" rx="4.5" ry="3" fill="#c4854c" />
      <ellipse cx="65.5" cy="17" rx="1.5" ry="1" fill="#1a1a1a" />
      <circle cx="59" cy="14" r="1.5" fill="#f5f0e8" />
      <circle cx="59" cy="14" r="0.8" fill="#1a1a1a" />
      <ellipse cx="53" cy="11" rx="3.5" ry="5" fill="#2d2926" transform="rotate(-15 53 11)" />

      {/* Sitting legs */}
      <line x1="50" y1="30" x2="50" y2="42" stroke="#2d2926" strokeWidth="3" strokeLinecap="round" />
      <line x1="44" y1="30" x2="44" y2="42" stroke="#2d2926" strokeWidth="3" strokeLinecap="round" />
      <line x1="36" y1="30" x2="36" y2="42" stroke="#2d2926" strokeWidth="3" strokeLinecap="round" />
      <line x1="30" y1="30" x2="30" y2="42" stroke="#2d2926" strokeWidth="3" strokeLinecap="round" />

      {/* Tail resting */}
      <path d="M22 18 Q18 12 14 14" stroke="#2d2926" strokeWidth="3" strokeLinecap="round" fill="none" />
    </svg>
  );
}

export const FelixDog = memo(function FelixDog({ state, label }: FelixDogProps) {
  return (
    <div className="flex flex-col items-center gap-0.5">
      <div className="relative h-[44px] w-[200px] overflow-hidden">
        <div
          className={
            state === "walking"
              ? "animate-felix-trot absolute"
              : state === "dead"
                ? "absolute left-1/2 -translate-x-1/2"
                : "absolute left-1/2 -translate-x-1/2"
          }
        >
          {state === "walking" && <WalkingDog />}
          {state === "dead" && <DeadDog />}
          {state === "idle" && <IdleDog />}
        </div>
      </div>
      {label && (
        <span className="text-[10px] text-slate-500 dark:text-slate-400 select-none">
          {label}
        </span>
      )}
    </div>
  );
});

export type { DogState };
